/**
 * The public docs portal (`createAppRouter`, the "Ver portal público" link)
 * lives on the public docs host, not on whichever origin an administrator or
 * anonymous visitor happens to be using (`VITE_DOCS_PUBLIC_ORIGIN`, set by
 * the deploy pipeline to `https://docs.be-flow.com`). On that host, the edge
 * proxies `/api/public/docs/*` to the backend, so the portal can call its own
 * origin's `/api` and stay same-origin under the CSP instead of depending on
 * the application host's `connect-src`.
 *
 * Both helpers read the env var at CALL time rather than caching it in a
 * module-level constant, so a single build can be exercised against either
 * origin in tests via `vi.stubEnv`, and so an unset or invalid value falls
 * back cleanly instead of throwing.
 */

/**
 * The configured public docs origin, normalized to an origin string (scheme
 * + host + port, no path) with `URL#origin`, or `null` when
 * `VITE_DOCS_PUBLIC_ORIGIN` is unset, empty, unparsable, or not an
 * `http:`/`https:` URL. A non-http scheme (e.g. `javascript:...`) parses
 * without throwing but reports `origin` as the literal string `"null"`, so
 * the scheme is checked explicitly rather than trusting `origin` alone.
 *
 * make-published-docs-public spec, "In-app links to the public portal use
 * the configured docs origin": this is the one function both
 * `createAppRouter()` (the hostname check that swaps in the public docs
 * route table) and the "Ver portal público" link need directly, with no
 * same-origin fallback — a build with no docs origin configured hides that
 * link entirely rather than pointing it at the wrong host.
 */
export function configuredDocsOrigin(): string | null {
  const raw = import.meta.env.VITE_DOCS_PUBLIC_ORIGIN;
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  return url.origin;
}

/**
 * A reader-facing label for where the public portal lives: the configured
 * docs origin's bare host when set, or a neutral Spanish fallback phrase
 * when it isn't — never a hard-coded hostname. Used by copy that tells an
 * administrator what "público" means (the "Público" checkbox's helper text
 * in `ServiceOperationNav`, and the publish confirmation copy in
 * `GuideEditor`). Read at call time, like `configuredDocsOrigin()` itself,
 * so a single build can be exercised against either state in tests via
 * `vi.stubEnv`.
 */
export function publicPortalHostLabel(): string {
  const origin = configuredDocsOrigin();
  return origin ? new URL(origin).host : "el portal público";
}

/**
 * The base URL `publicGet` SHALL send public docs requests to: the current
 * origin's own `/api` only when the viewer is running on the configured docs
 * origin, and the configured `VITE_API_BASE_URL` everywhere else — including
 * when no docs origin is configured at all, which keeps local development
 * and any build without the new variable working exactly as before.
 */
export function publicApiBase(): string {
  if (configuredDocsOrigin() === window.location.origin) {
    return `${window.location.origin}/api`;
  }
  return (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
}
